import { useCallback, useEffect, useState, useMemo } from "react";
import {
  useHighlightsStore,
  HighlightType,
  NewHtmlHighlight,
} from "../animator/useHighlightsStore";
import { useCuepointsStore } from "../animator/useCuepointsStore";
import {
  loadProjectAsset,
  extractSourcesHighlightsCuepoints,
  defaultRoughNotation,
} from "./helpers/helpers";
import { StoryboardHighlights, BrandKit } from "../../types";
import { v4 as uuidv4 } from "uuid";
import { GetOneResponse } from "@refinedev/core";

interface UseHighlightsManagerProps {
  highlightsAssetPath?: string;
  documentId?: string;
  brandKit?: GetOneResponse<BrandKit>;
}

export const useHighlightsManager = ({
  highlightsAssetPath,
  documentId,
  brandKit,
}: UseHighlightsManagerProps) => {
  const [storyboardHighlights, setStoryboardHighlights] =
    useState<StoryboardHighlights>(null);

  const { setCuepoints } = useCuepointsStore();

  const {
    setCurrentHighlight,
    setHighlights,
    setSectionHighlights,
    addHighlight,
  } = useHighlightsStore();

  const roughNotation = useMemo(
    () => ({
      ...defaultRoughNotation,
      color:
        (brandKit?.data?.palette.highlight_color ||
          brandKit?.data?.palette.primary_color) ??
        defaultRoughNotation.color,
    }),
    [
      brandKit?.data?.palette.highlight_color,
      brandKit?.data?.palette.primary_color,
    ]
  );

  // Load highlights from asset
  useEffect(() => {
    if (!highlightsAssetPath) return;
    const loadHighlights = async () => {
      try {
        const data = await loadProjectAsset(highlightsAssetPath);
        if (data) {
          setStoryboardHighlights(data);
        }
      } catch (error) {
        console.error("Error loading highlights", error);
      }
    };
    loadHighlights();
  }, [highlightsAssetPath]);

  // Process storyboard highlights
  useEffect(() => {
    if (!storyboardHighlights?.section_highlights) return;

    try {
      const { sources, highlights, cuepoints } =
        extractSourcesHighlightsCuepoints(storyboardHighlights);
      setHighlights(highlights);
      setCuepoints(cuepoints);
      setSectionHighlights(sources);
    } catch (error) {
      console.error("Error processing highlights:", error);
    }
  }, [storyboardHighlights, setHighlights, setCuepoints, setSectionHighlights]);

  const handleAddHighlight = useCallback(
    async (highlight: NewHtmlHighlight, type: HighlightType = "html") => {
      const newHighlight = {
        ...highlight,
        id: uuidv4(),
        type,
        generated: false,
      };

      addHighlight(newHighlight);
      setCurrentHighlight(newHighlight);

      const newCuepoint = {
        highlightId: newHighlight.id,
        start: 0,
        duration: 1,
        sourceId: newHighlight.id,
        documentId,
        roughNotation,
      };

      return { highlight: newHighlight, cuepoint: newCuepoint };
    },
    [addHighlight, setCurrentHighlight, documentId, roughNotation]
  );

  return {
    storyboardHighlights,
    handleAddHighlight,
  };
};
